import { Component, Inject, OnInit } from '@angular/core';
import { BaseCustomDialog } from '../base-custom-dialog';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ACTION_DATA_SERVICE, ActionDataService } from '../../../../services/action/data.service';
import { DateTimeHelper, TimePeriodType } from '../../../../helper/date-time.helper';
import { ActionIconHelper } from '../../../../helper/action-icon.helper';
import { LEADS_DATA_SERVICE, LeadsDataService } from '../../../../services/leads/leads-data.service';
import { ICompanyContacts, IContact } from '../../../../models/leads/leads.model';
import { AppointmentsHelper } from '../../../../helper/appointments.helper';
import { ResourceCountry, ResourceProvinces } from '../../../../models/resources/resources.model';
import { AddressHelper } from '../../../../helper/address.helper';
import { ValidatorHelper } from '../../../../helper/validator.helper';
import { PROFILE_DATA_ID } from '../../../../constants/constants';

@Component({
  selector: 'fi-visit-planner-popup',
  templateUrl: './visit-planner-popup.component.html',
  styleUrls: ['./visit-planner-popup.component.scss'],
  standalone: false,
})
export class VisitPlannerPopupComponent extends BaseCustomDialog implements OnInit {
  contactName = '';
  leadId: number;
  selectedTypeId: number;
  readonly dateOptionTypes = DateTimeHelper.datePeriodTypes;
  searchedText = '';
  note = '';
  selectedDate: Date;
  title: string;
  iconUrl = ActionIconHelper.getActionIconUrl('PLANNER_VISIT', false);
  darkIconUrl = ActionIconHelper.getActionIconUrl('PLANNER_VISIT', true);
  submitting: boolean;
  companyContacts: ICompanyContacts[];
  contactToDisplay: IContact;
  appointmentExists = false;
  inValidNote = false;
  resourceCountries: ResourceCountry[];
  resourceProvinces: ResourceProvinces[];
  provinceCountryCodes: string[] = [];

  countryCode = '';
  provinceCode = '';
  address = '';
  postCode = '';
  locality = '';

  sameAsContactAddress = true;

  addressError = false;
  postCodeError = false;
  localityError = false;
  isPlanerVisitMove = false;
  isExternal = false;
  appointmentTypeId = -1;

  constructor(
    public dialogRef: MatDialogRef<VisitPlannerPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: {
      contactName: string,
      leadId: number,
      leadTypeId: number,
      title: string,
      isPlanerVisitMove?: boolean,
      isExternal?: boolean,
    },
    @Inject(ACTION_DATA_SERVICE) private actionDataService: ActionDataService,
    @Inject(LEADS_DATA_SERVICE) private leadsDataService: LeadsDataService,
  ) {
    super(dialogRef);

    this.contactName = dialogData?.contactName || '';
    this.leadId = dialogData?.leadId;
    this.title = dialogData?.title;
    this.isPlanerVisitMove = dialogData?.isPlanerVisitMove;
    this.isExternal = dialogData?.isExternal;

    actionDataService.getAppointmentTypes().subscribe(( { success, data } ) => {
      if ( success ) {
        this.appointmentTypeId = data.find(item => item.Name === 'VOR ORT')?.Id ?? -1;
      }
    });

    this.leadsDataService.getLeadById(this.leadId).subscribe(( { success, data } ) => {
      if ( success ) {
        this.companyContacts = data.CompanyContacts;
        this.contactToDisplay = data.Contact;
        this.onSameAsContactAddressChange();
      }
    });
    AddressHelper.getCountries().then(countries => {
      this.resourceCountries = countries;
    });


    AddressHelper.getProvinceCountryCodes().then(data => {
      this.provinceCountryCodes = data;
    });
  }

  onDateSelect( date: Date, time = false ): void {
    this.selectedTypeId = null;

    if ( !date ) {
      this.selectedDate = null;
      return;
    }

    if ( !this.selectedDate ) {
      this.selectedDate = date;
    } else if ( !time ) {
      this.selectedDate.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
    } else {
      this.selectedDate.setHours(date.getHours(), date.getMinutes());
    }

    this.selectedDate = new Date(this.selectedDate);

    this.appointmentExists = AppointmentsHelper.getAppointments(this.selectedDate, this.appointmentExists);
  }

  setPeriods( $event ): void {
    this.selectedTypeId = $event;
    this.selectedDate = DateTimeHelper.getDateAfterPeriod(this.dateOptionTypes.find(item => item.Id === +$event));
    this.appointmentExists = AppointmentsHelper.getAppointments(this.selectedDate, this.appointmentExists);
  }

  onDateOptionSelected( type: TimePeriodType ): void {
    this.selectedTypeId = type.Id;
    this.selectedDate = DateTimeHelper.getDateAfterPeriod(type);
    this.appointmentExists = AppointmentsHelper.getAppointments(this.selectedDate, this.appointmentExists);
  }

  async ngOnInit(): Promise<void> {
    const userId = await sessionStorage.getItem(PROFILE_DATA_ID);
    AppointmentsHelper.getDbAppointments(this.actionDataService, userId);
  }

  closeDialog(): void {
    this.clearClassAndCloseDialog(this.dialogRef);
  }

  get formIsValid(): boolean {
    return this.appointmentTypeId !== -1 && !!this.leadId && !!this.selectedDate && !this.submitting && !this.inValidNote;
  }

  inValidMaxLength(): boolean {
    this.inValidNote = this.note.length > 2000;
    return this.inValidNote;
  }

  submit(): void {
    if ( this.formIsValid && !this.hasInvalidAddressField() ) {
      this.submitting = true;
      this.actionDataService.visitPlanner(
        this.leadId,
        this.note,
        this.selectedDate,
        this.appointmentTypeId,
        this.isPlanerVisitMove,
        this.isExternal,
        {
          CountryCode: this.countryCode,
          ZipCode: this.postCode,
          City: this.locality,
          Address: this.address,
          ProvinceCode: this.provinceCode,
        },
      )
        .subscribe(( { success, data } ) => {
          if ( success ) {
            this.clearClassAndCloseDialog(this.dialogRef, 'remove');
          }

          this.submitting = false;
        });
    }
  }

  hasInvalidAddressField(): boolean {
    if ( this.sameAsContactAddress ) {
      this.addressError = this.postCodeError = this.localityError = false;
      return false;
    }

    this.addressError = this.address && !this.address.match(ValidatorHelper.addressValidator);
    this.postCodeError = this.postCode && !this.postCode.match(ValidatorHelper.zipValidator);
    this.localityError = this.locality && !this.locality.match(ValidatorHelper.cityValidator);

    return this.addressError || this.postCodeError || this.localityError;
  }

  setCountyCode( countryCode: string ): void {
    this.countryCode = countryCode;

    AddressHelper.getProvinces(countryCode).then(data => {
      this.resourceProvinces = data;

      if ( !this.resourceProvinces.some(x =>
        x.Code.toLowerCase() === this.provinceCode?.toLowerCase()
      ) ) {
        this.provinceCode = '';
      }
    })

  }

  setProvinceCode( provinceCode: string ): void {
    this.provinceCode = provinceCode;
  }

  onSameAsContactAddressChange(): void {
    if ( this.sameAsContactAddress ) {
      this.provinceCode = this.contactToDisplay?.ProvinceCode ?? '';
      this.address = this.contactToDisplay?.Address ?? '';
      this.postCode = this.contactToDisplay?.ZipCode ?? '';
      this.locality = this.contactToDisplay?.City ?? '';
      this.setCountyCode(this.contactToDisplay?.CountryCode ?? '');
    } else {
      this.provinceCode = '';
      this.address = '';
      this.postCode = '';
      this.locality = '';
      this.setCountyCode('');
    }
  }
}
